import React from 'react'

import { DashboardFilter } from 'types/GlobalDashboardFilter'
import { SxProps } from '@mui/material'
import { TDataDialogView } from '../types'
import { TMetricView } from 'types/GlobalMetric'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import NavigationItem from 'components_new/molecules/NavigationItem'
import SectionHeader from 'components_new/molecules/SectionHeader'

import { useTranslation } from 'language/translator'

import { DETAILS_DISABLE_TEXT } from 'utils/constants'
import { getParsedDate } from 'utils/dateParser'

interface Props {
  activeViewId?: string
  // Display labels for metricView.filters, parallel/index-aligned to each
  // attribute's value array. Only present for attributes whose filter value
  // is a raw id distinct from its human-readable name (e.g. "handläggare").
  // Display-only — the raw value is still what's used for querying/matching.
  filterLabels?: Record<string, string[]>
  fromDate?: string | null
  localFilters?: DashboardFilter
  metricView?: TMetricView & { title: string }
  onChangeView?: (id: string) => void
  onDeleteLocalFilter: (params: { id: string; value: string }) => void
  sx?: SxProps
  toDate?: string | null
  // The selectable views, when the source has more than one.
  views?: TDataDialogView[]
}

const LeftSidebar = (props: Props) => {
  const {
    activeViewId,
    filterLabels,
    localFilters,
    metricView,
    onChangeView,
    onDeleteLocalFilter,
    sx,
    views
  } = props

  const { lang, t } = useTranslation()

  const fromDate = getParsedDate(props.fromDate ?? null, lang) || null
  const toDate = getParsedDate(props.toDate ?? null, lang) || null

  const timePeriodLabel =
    fromDate && toDate ? `${fromDate} - ${toDate}` : fromDate ?? toDate ?? null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...sx }}>
      {/*-- views --*/}
      {views && views.length > 0 ? (
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
          {views.map((item) => (
            <TooltipWrapper
              key={item.id}
              title={item.disabled ? DETAILS_DISABLE_TEXT : undefined}
            >
              <NavigationItem
                active={item.id === activeViewId}
                item={{
                  disabled: item.disabled,
                  onClick: () => {
                    if (onChangeView) onChangeView(item.id)
                  },
                  title: item.title
                }}
              />
            </TooltipWrapper>
          ))}
        </Box>
      ) : null}

      {/*-- filters --*/}
      {metricView && Object.keys(metricView.filters).length > 0 ? (
        <Box>
          <SectionHeader size="xsmall" title={t('filters', 'filters')} />

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5
            }}
          >
            {Object.entries(metricView.filters).flatMap(
              ([attribute, item], i) => {
                const isLocalFilter = (localFilters ?? []).some(
                  (f) => f.attribute === attribute
                )
                const attributeLabels = filterLabels?.[attribute]

                return item.map((value, j) => (
                  <Chip
                    color="primary"
                    disabled={
                      localFilters && localFilters.length > 0
                        ? !isLocalFilter
                        : undefined
                    }
                    key={`${i}-${j}`}
                    label={attributeLabels?.[j] ?? value}
                    onDelete={
                      isLocalFilter
                        ? () => onDeleteLocalFilter({ id: attribute, value })
                        : undefined
                    }
                    size="xsmall"
                  />
                ))
              }
            )}
          </Box>
        </Box>
      ) : null}

      {/*-- time period --*/}
      {timePeriodLabel ? (
        <Box>
          <SectionHeader
            size="xsmall"
            title={t('date_filters', 'time_period')}
          />
          <Chip color="primary" label={timePeriodLabel} size="xsmall" />
        </Box>
      ) : null}
    </Box>
  )
}

export default LeftSidebar
